import { House } from '@/client/types.gen';
import { LuArchive, LuHouse, LuKeyRound, LuPencil } from 'react-icons/lu';
import { isDraftHouse } from '@/utils/houseUtils';
import { ClassifiedHouse, isOwnedArchiveCopy, isOwnedProperty } from '@/utils/properties/propertyGroupingUtils';
import { PropertySelectorGroupKey } from '@/components/properties/selector/propertySelectorSections.ts';

/** Icon tile size for the square grid-view card. */
export const CARD_ICON_SIZE = '48px';
/** Icon tile size for full-width row cards (list view and claimable transfers). */
export const LIST_CARD_ICON_SIZE = 14;

/** Min height for grid-view cards (property tiles and claimable transfers). */
export const GRID_CARD_MIN_HEIGHT = '132px';
/** Min height for full-width row cards (list view and claimable transfers). */
export const LIST_CARD_MIN_HEIGHT = '88px';

const PROPERTY_CARD_MOBILE_SHADOW = '0 1px 2px rgba(0, 0, 0, 0.04), 0 2px 4px rgba(0, 0, 0, 0.04)';
const PROPERTY_CARD_MOBILE_LIST_SHADOW = '0 1px 2px rgba(0, 0, 0, 0.04)';
const PROPERTY_CARD_MOBILE_HOVER_SHADOW = '0 2px 4px rgba(0, 0, 0, 0.06), 0 6px 14px rgba(0, 0, 0, 0.08)';

export const PROPERTY_GRID_CARD_SHADOW = {
	base: PROPERTY_CARD_MOBILE_SHADOW,
	md: 'sm',
} as const;
export const PROPERTY_LIST_CARD_SHADOW = {
	base: PROPERTY_CARD_MOBILE_LIST_SHADOW,
	md: 'xs',
} as const;
// A locked card doesn't lift on hover, so it keeps the resting grid-card elevation.
export const PROPERTY_CARD_LOCKED_HOVER_SHADOW = PROPERTY_GRID_CARD_SHADOW;
export const PROPERTY_CARD_HOVER_SHADOW = {
	base: PROPERTY_CARD_MOBILE_HOVER_SHADOW,
	md: 'md',
} as const;

export interface PropertyCardProps {
	property: ClassifiedHouse;
	isLocked: boolean;
	isSelected: boolean;
	isTransferring?: boolean;
	hasUnreadNotification?: boolean;
	onSelect: () => void;
	onLockedClick?: () => void;
	onDelete: () => void;
}

type HouseUserRoleWithExpiry = NonNullable<House['user_role']> & {
	expires_at?: string | null;
};

export const getAccessExpiresAt = (property: House) => {
	return (property.user_role as HouseUserRoleWithExpiry | null | undefined)?.expires_at;
};

/**
 * Pick which selector group a property belongs to so its card icon matches the selector's section
 * icon (house / key / archive). Only the user's *own* retained clone reads as Archive; a clone
 * shared with the user by someone else falls through to Shared (its copy-ness is shown by a corner
 * badge, not the base icon). Archive is checked before owned since an owned clone also reports as
 * owned.
 */
const resolvePropertyGroupKey = (property: House, userId?: number): PropertySelectorGroupKey => {
	if (isOwnedArchiveCopy(property, userId)) return PropertySelectorGroupKey.Archive;
	if (isOwnedProperty(property, userId)) return PropertySelectorGroupKey.Owned;
	return PropertySelectorGroupKey.Shared;
};

const resolvePropertyCardIcon = (groupKey: PropertySelectorGroupKey) => {
	if (groupKey === PropertySelectorGroupKey.Shared) return LuKeyRound;
	if (groupKey === PropertySelectorGroupKey.Archive) return LuArchive;
	return LuHouse;
};

export const getPropertyMeta = (property: House, userId?: number, isTransferring = false) => {
	const icon = resolvePropertyCardIcon(resolvePropertyGroupKey(property, userId));

	if (isDraftHouse(property)) {
		return { icon: LuPencil, badge: 'Draft', colorPalette: 'yellow' as const };
	}

	if (isTransferring) {
		return { icon, badge: 'Transferring', colorPalette: 'orange' as const };
	}

	return { icon };
};
